import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Alert,
  Button,
  CircularProgress,
  Chip,
  Divider,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  type SelectChangeEvent,
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Sync as SyncIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../contexts/ApiContext';

interface CronConfig {
  id: number;
  enabled: boolean;
  schedule_cron: string;
  last_run_at: string | null;
  last_run_status: string | null;
  last_run_message: string | null;
  last_run_details: {
    revolut?: { transactions?: Record<string, number>; expenses?: Record<string, number>; success?: boolean };
    bookings?: { synced_count?: number; errors?: string[]; success?: boolean };
  } | null;
}

const CRON_SCHEDULE_LABELS: Record<string, string> = {
  '0 0 * * *': 'Daily at midnight (UTC)',
  '0 1 * * *': 'Daily at 1:00 AM (UTC)',
  '0 6 * * *': 'Daily at 6:00 AM (UTC)',
  '0 12 * * *': 'Daily at noon (UTC)'
};

export const Settings: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [config, setConfig] = useState<CronConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncTxUserSettings, setSyncTxUserSettings] = useState<{
    nightly_cron_user_id: number | null;
    resolved_nightly_cron_user_id: number | null;
    eligible_users: Array<{ id: number; email: string; first_name: string; last_name: string }>;
  } | null>(null);
  const [nightlySelectVal, setNightlySelectVal] = useState<string>('');
  const [syncUserSaving, setSyncUserSaving] = useState(false);
  const [saving, setSaving] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const fetchCronAndNightly = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [statusRes, nightlyRes] = await Promise.all([
        api.get('/api/cron/status'),
        api.get('/api/cron/sync-settings'),
      ]);

      setConfig((statusRes?.data ?? null) as CronConfig | null);

      const nightlyBody = nightlyRes?.data;
      const nightlyData = nightlyBody?.data;
      if (!nightlyBody?.success || !nightlyData) {
        throw new Error(
          nightlyBody?.message ||
            (typeof nightlyBody?.error === 'string' ? nightlyBody.error : null) ||
            'Failed to load cron nightly user settings'
        );
      }
      setSyncTxUserSettings({
        nightly_cron_user_id: nightlyData.nightly_cron_user_id ?? null,
        resolved_nightly_cron_user_id: nightlyData.resolved_nightly_cron_user_id ?? null,
        eligible_users: Array.isArray(nightlyData.eligible_users) ? nightlyData.eligible_users : [],
      });
      const pref = nightlyData.nightly_cron_user_id;
      setNightlySelectVal(pref == null ? '' : String(pref));
    } catch (err: unknown) {
      const ax = err as {
        response?: {
          status?: number;
          data?: { error?: unknown; message?: string; code?: string };
        };
        message?: string;
      };
      const d = ax.response?.data;
      let msg =
        (typeof d?.error === 'string' ? d.error : null) ||
        d?.message ||
        ax.message ||
        'Failed to load settings';
      const code = d?.code;
      if (`${code}` === 'cron_sync_settings_owner_required' || ax.response?.status === 403) {
        navigate('/', { replace: true });
        return;
      }
      setError(`${msg}`);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== 'owner') {
      navigate('/', { replace: true });
      return;
    }
    fetchCronAndNightly();
  }, [authLoading, user, navigate, fetchCronAndNightly]);

  const handleToggleEnabled = async (enabled: boolean) => {
    if (!config) return;
    try {
      setSaving(true);
      setError('');
      await api.put('/api/cron/config', { enabled });
      setConfig({ ...config, enabled });
      setMessage(enabled ? 'Daily sync enabled' : 'Daily sync disabled');
      setTimeout(() => setMessage(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTriggerSync = async () => {
    try {
      setTriggering(true);
      setError('');
      setMessage('');
      await api.post('/api/cron/sync');
      setMessage('Sync triggered successfully');
      await fetchCronAndNightly();
      setTimeout(() => setMessage(''), 5000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to trigger sync');
    } finally {
      setTriggering(false);
    }
  };

  const formatLastRun = (iso: string | null) => {
    if (!iso) return 'Never';
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short'
      });
    } catch {
      return iso;
    }
  };

  const handleNightlyImportUserChange = async (event: SelectChangeEvent<string>) => {
    const v = event.target.value;
    setNightlySelectVal(v);
    try {
      setSyncUserSaving(true);
      setError('');
      const nightly_cron_user_id = v === '' ? null : Number(v);
      const res = await api.patch('/api/cron/sync-settings', { nightly_cron_user_id });
      const data = res?.data?.data;
      if (res?.data?.success && data) {
        setSyncTxUserSettings((prev) =>
          prev
            ? {
                ...prev,
                nightly_cron_user_id: data.nightly_cron_user_id ?? nightly_cron_user_id,
                resolved_nightly_cron_user_id:
                  data.resolved_nightly_cron_user_id ?? prev.resolved_nightly_cron_user_id,
              }
            : prev
        );
        setMessage(data.nightly_cron_user_id == null ? 'Using default importer (lowest id with tx import)' : 'Nightly importer updated');
        setTimeout(() => setMessage(''), 4000);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Could not save nightly importer');
      fetchCronAndNightly();
    } finally {
      setSyncUserSaving(false);
    }
  };

  if (authLoading) {
    return (
      <Container maxWidth="md" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom fontWeight="bold">
        Settings
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Configure daily data sync and view status
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {message && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>
          {message}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <ScheduleIcon color="primary" />
            <Typography variant="h6">Daily Data Sync</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Automatically pull Revolut bank data and Bookings (Airbnb/VRBO iCal) once per day at midnight UTC.
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={config?.enabled ?? true}
                onChange={(e) => handleToggleEnabled(e.target.checked)}
                disabled={saving}
                color="primary"
              />
            }
            label={config?.enabled ? 'Enabled' : 'Disabled'}
          />

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Schedule
            </Typography>
            <Typography variant="body1">
              {CRON_SCHEDULE_LABELS[config?.schedule_cron || '0 0 * * *'] || config?.schedule_cron || 'Daily at midnight (UTC)'}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {syncTxUserSettings && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Nightly cron — Revolut import user
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              The midnight cron job runs as whichever active user has the <code>tx_import</code> capability — either
              the selected account below or, by default, the lowest user id among eligible importers.
            </Typography>
            <FormControl fullWidth sx={{ maxWidth: 480 }} disabled={syncUserSaving}>
              <InputLabel id="nightly-cron-import-user-label">Import as user</InputLabel>
              <Select<string>
                labelId="nightly-cron-import-user-label"
                label="Import as user"
                value={nightlySelectVal}
                onChange={handleNightlyImportUserChange}
              >
                <MenuItem value="">
                  <em>Default (lowest eligible id)</em>
                </MenuItem>
                {syncTxUserSettings.eligible_users.map((u) => (
                  <MenuItem key={u.id} value={String(u.id)}>
                    {u.first_name} {u.last_name} ({u.email})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Effective user id after rules:
              </Typography>
              <Chip size="small" label={String(syncTxUserSettings.resolved_nightly_cron_user_id ?? '—')} />
              {syncUserSaving && <CircularProgress size={20} />}
            </Box>
          </CardContent>
        </Card>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <SyncIcon color="primary" />
            <Typography variant="h6">Sync Status</Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Last run
              </Typography>
              <Typography variant="body1">
                {formatLastRun(config?.last_run_at ?? null)}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Status
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {config?.last_run_status === 'success' && (
                  <Chip
                    icon={<CheckCircleIcon />}
                    label="Success"
                    color="success"
                    size="small"
                  />
                )}
                {config?.last_run_status === 'error' && (
                  <Chip
                    icon={<ErrorIcon />}
                    label="Error"
                    color="error"
                    size="small"
                  />
                )}
                {config?.last_run_status === 'skipped' && (
                  <Chip label="Skipped" size="small" />
                )}
                {(!config?.last_run_status || config.last_run_status === 'partial') && config?.last_run_at && (
                  <Chip label="Partial" color="warning" size="small" />
                )}
                {!config?.last_run_at && (
                  <Chip label="Never run" size="small" variant="outlined" />
                )}
              </Box>
            </Grid>
          </Grid>

          {config?.last_run_message && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Message
              </Typography>
              <Typography variant="body2">{config.last_run_message}</Typography>
            </Box>
          )}

          {config?.last_run_details && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Last run details
              </Typography>
              <Box
                component="pre"
                sx={{
                  p: 1.5,
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                  fontSize: '0.8rem',
                  overflow: 'auto'
                }}
              >
                {JSON.stringify(config.last_run_details, null, 2)}
              </Box>
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          <Button
            variant="contained"
            startIcon={triggering ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
            onClick={handleTriggerSync}
            disabled={triggering}
          >
            {triggering ? 'Syncing...' : 'Run sync now'}
          </Button>
        </CardContent>
      </Card>

      <Alert severity="info" sx={{ mt: 2 }}>
        <Typography variant="body2">
          The cron job runs on the production host at midnight UTC. Ensure <code>CRON_SECRET</code> is set in the API
          environment for the scheduled job to authenticate. Nightly Playwright E2E (GitHub Actions) posts results to
          the same history via <code>POST /api/cron/e2e-report</code>; see the Dashboard &quot;Cron Jobs&quot; card and
          email <code>[Family Finance E2E]</code> when SMTP is configured.
        </Typography>
      </Alert>
    </Container>
  );
};
